import * as React from "react";
import { usePortalSeo, useEnrolmentUiConfig } from "../config";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiClient,
  ApiError,
  PaymentRef,
  formatNameList,
  PAY_METHOD_DEFAULT_ORDER,
  type Enrolment,
  type EnrolmentUpdate,
  type PayMethod,
  type PaymentIntentResult,
} from "@alight/contracts";
import {
  Section,
  PageHeader,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Button,
  Badge,
  Label,
  Textarea,
  cn,
} from "@alight/ui";
import { CheckCircle2, Clock, Copy, Check } from "lucide-react";
import { CardCheckout } from "../components/CardCheckout";

/**
 * /pay/$ref — settle an outstanding registration fee.
 *
 * Mirrors the enrolment journey's payment step: the parent picks HOW to pay —
 * card, bank transfer, cash in person, or "I've already paid" — and each path
 * lands in the same state the journey would have produced:
 *   card                    → POST /api/payments/checkout → redirect to Stripe,
 *                              the WEBHOOK marks it paid (never the redirect)
 *   payid  + "I have paid"  → POST /api/payments/intent   → pending_review
 *   cash   + handover time  → PATCH /api/enrolments/:ref  → stays unpaid,
 *                              receipted in person
 *   already + note          → PATCH /api/enrolments/:ref  → pending_review,
 *                              account manager verifies
 * The PATCH paths are ownership-checked server-side, so cash/already need the
 * parent to be signed in (card and bank transfer work signed out — they only
 * act against the reference).
 *
 * WHICH options appear, and in what order, is host-configured
 * (`payments.method_order`) and arrives already filtered on
 * GET /api/payment-config — this file renders the server's list, it does not
 * decide it.
 */

/* Instant-render fallback while GET /api/payments/:ref/summary is in flight —
 * the real amount owing (which can differ after a part-payment) always comes
 * from the server. */
const FALLBACK_AMOUNT_CENTS = 5000;

/* Bank-transfer details and the "allow up to N business days" window come
 * LIVE from GET /api/payment-config (host-editable in the admin portal's
 * Settings page). The VITE_ vars below are only the instant-render fallback
 * while that request is in flight (or if it fails). */
const CONTACT_EMAIL = "alightcreative@htbh.org.au";
const BANK_ACCOUNT_NAME =
  import.meta.env.VITE_BANK_ACCOUNT_NAME ?? "Arise Anglican Church - Alight Creative";
const BANK_BSB = import.meta.env.VITE_BANK_BSB ?? "082-135";
const BANK_ACCOUNT_NUMBER = import.meta.env.VITE_BANK_ACCOUNT_NUMBER ?? "9407 8704 2";
const CLEAR_DAYS = Number(import.meta.env.VITE_PAYMENT_CLEAR_DAYS ?? 3);
const HOST_CONTACT = import.meta.env.VITE_HOST_CONTACT ?? "Loretta";

/** Concrete cash handovers a parent can commit to (same copy as /enrol). */
const CASH_OPTIONS = [
  "At the first session",
  "At a Sunday service before it starts",
] as const;

/* Same-origin API by default; override for split deployments. */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

/** Chooser copy, keyed by method so the host can reorder without touching JSX.
 *  `desc` takes the payment contact because two of them name that person. */
const METHOD_COPY: Record<PayMethod, { title: string; desc: (contact: string) => string }> = {
  card: {
    title: "Card",
    desc: () => "Pay now by card — your place is confirmed straight away.",
  },
  payid: {
    title: "Bank transfer",
    desc: () => "Transfer now from your banking app — takes a few days to clear.",
  },
  cash: {
    title: "Cash",
    desc: (contact) => `Hand it to ${contact} in person — pick when below.`,
  },
  already: {
    title: "I've already paid",
    desc: () => "Paid earlier? Tell us so we can match it.",
  },
};

function formatAud(cents: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: Number.isInteger(cents / 100) ? 0 : 2,
  }).format(cents / 100);
}

export function PayPage() {
  usePortalSeo("Payment");
  const { registerPath } = useEnrolmentUiConfig();
  const { ref } = useParams({ strict: false }) as { ref: string };
  /* Stripe's return hop: ?paid=1 on success, ?cancelled=1 if they backed out.
   * Neither proves anything — see the polling on `summary` below. */
  const { paid: backFromStripe, cancelled: cardCancelled } = useSearch({ strict: false }) as {
    paid?: boolean;
    cancelled?: boolean;
  };
  const refCheck = PaymentRef.safeParse(ref);
  const qc = useQueryClient();

  const client = React.useMemo(() => apiClient(API_BASE_URL), []);

  /* Signed-in parent? Cash and "already paid" are recorded against the
   * registration itself, so the server insists on knowing who's asking. */
  const me = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => client.authMe(),
    retry: false,
  });
  const signedIn = Boolean(me.data?.email);

  const intent = useMutation<PaymentIntentResult, Error, void>({
    mutationFn: () => client.paymentIntent({ ref }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my", "enrolments"] }),
  });

  const update = useMutation<Enrolment, Error, EnrolmentUpdate>({
    mutationFn: (patch) => client.updateEnrolment(ref, patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my", "enrolments"] }),
  });

  /* Card: hand off to Stripe's hosted page. We leave the site entirely, so
   * there's no success state to render here — the parent returns to ?paid=1
   * and the webhook is what actually marks the enrolment paid. */
  /* Stripe calls these when it mounts the embedded form, so a session is
   * created only once the parent is actually looking at the card fields. */
  const fetchSingleClientSecret = React.useCallback(async () => {
    const session = await client.paymentCheckout({ ref });
    if (!session.client_secret) throw new Error("no client secret");
    return session.client_secret;
  }, [client, ref]);

  const fetchGroupClientSecret = React.useCallback(async () => {
    const session = await client.paymentCheckoutGroup();
    if (!session.client_secret) throw new Error("no client secret");
    return session.client_secret;
  }, [client]);

  /* Which form is mounted: this child alone, or the whole family. */
  const [cardScope, setCardScope] = React.useState<"single" | "group">("single");

  /* Does this parent have other children still owing? Only meaningful when
   * signed in — the server derives the family from the session, never from
   * this reference, so a stranger holding one ref learns nothing. */
  const group = useQuery({
    queryKey: ["payment-group-summary"],
    queryFn: () => client.paymentGroupSummary(),
    enabled: signedIn,
    retry: false,
  });

  /* One payment for the whole family: fewer fixed fees, one receipt, and one
   * thing for the account manager to reconcile instead of three. */


  const config = useQuery({
    queryKey: ["payment-config"],
    queryFn: () => client.paymentConfig(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const bankAccountName = config.data?.bank_account_name ?? BANK_ACCOUNT_NAME;
  const bankBsb = config.data?.bank_bsb ?? BANK_BSB;
  const bankAccountNumber = config.data?.bank_account_number ?? BANK_ACCOUNT_NUMBER;
  const hostContact = config.data?.contact_name ?? HOST_CONTACT;
  /* Server decides which options exist and their order; card is already
   * stripped from this list when Stripe isn't configured. Falling back to the
   * built-in order (minus card) keeps the page usable if the config call fails. */
  const methodOrder: PayMethod[] =
    config.data?.method_order ?? PAY_METHOD_DEFAULT_ORDER.filter((m) => m !== "card");
  const stripePublishableKey = config.data?.stripe_publishable_key ?? null;

  /* What this reference actually owes right now (fee, part-payments, status). */
  const summary = useQuery({
    queryKey: ["payment-summary", ref],
    queryFn: () => client.paymentSummary(ref),
    enabled: refCheck.success,
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
    /* Back from Stripe: the webhook usually lands within a second or two, but
     * it races the redirect. Poll until the status flips rather than telling
     * the parent something we haven't confirmed. */
    refetchInterval: (query) =>
      backFromStripe && query.state.data?.payment_status !== "paid" ? 2000 : false,
  });
  const amountCents = summary.data?.balance_cents ?? FALLBACK_AMOUNT_CENTS;
  const programTitle = summary.data?.program_title ?? "Alight Creative";
  const unknownRef =
    summary.error instanceof ApiError &&
    (summary.error.status === 404 || summary.error.status === 400);

  /* ------------------------------- method chooser state ------------------ */
  const [method, setMethod] = React.useState<PayMethod | null>(null);
  const [transferDone, setTransferDone] = React.useState(false);
  const [cashWhen, setCashWhen] = React.useState<string | null>(null);
  const [alreadyNote, setAlreadyNote] = React.useState("");
  /* A previously saved cash choice shows as a "you're paying cash" state
   * until the parent asks to change it. */
  const [changingMethod, setChangingMethod] = React.useState(false);

  const clearDays = intent.data?.clear_days ?? config.data?.clear_days ?? CLEAR_DAYS;
  const isPaid = summary.data?.payment_status === "paid";
  const alreadyClaimed = update.isSuccess && update.data.payment_method === "already";
  const cashConfirmed =
    (update.isSuccess && update.data.payment_method === "cash") ||
    (!update.isSuccess &&
      !changingMethod &&
      summary.data?.payment_method === "cash" &&
      summary.data.payment_status === "unpaid");
  const isPending =
    !isPaid &&
    (intent.isSuccess || alreadyClaimed || summary.data?.payment_status === "pending_review");

  /* Back from Stripe but the webhook hasn't landed yet. Don't re-offer the
   * chooser — the parent has almost certainly just paid, and showing them
   * payment options again reads as "it didn't work". */
  const confirmingCard = Boolean(backFromStripe) && !isPaid;

  /* A zero balance on an unpaid enrolment means a free program (or a fee
   * already covered by recorded part-payments). Offering payment methods there
   * is nonsense, and card is worse than nonsense: Stripe rejects a $0 charge,
   * so the parent got a bare 409. */
  const nothingToPay = !isPaid && summary.isSuccess && amountCents <= 0;

  const needsChoice =
    !isPaid && !isPending && !cashConfirmed && !confirmingCard && !nothingToPay;

  /* Offer "pay for all" only when it genuinely covers more than this one
   * child AND this reference is part of the set (a stale group summary must
   * never let the button pay for something the parent isn't looking at). */
  const payAllCount =
    group.data && group.data.refs.includes(ref.toUpperCase()) ? group.data.count : 0;

  const submitting =
    update.isPending || intent.isPending;
  const canConfirm =
    !submitting &&
    ((method === "payid" && transferDone) ||
      (method === "cash" && cashWhen !== null) ||
      (method === "already" && alreadyNote.trim().length > 2));
  /* Card and bank transfer act against the reference alone; the other two edit
   * the record and so need a signed-in owner. */
  const needsSignIn = (method === "cash" || method === "already") && !signedIn;

  function confirm() {
    if (!canConfirm || !method) return;
    if (method === "payid") {
      intent.mutate();
    } else if (method === "cash") {
      update.mutate({ payment_method: "cash", cash_when: cashWhen ?? undefined });
    } else {
      update.mutate({ payment_method: "already", already_note: alreadyNote.trim() });
    }
  }

  const actionError = update.error ?? intent.error;
  const actionErrorText =
    actionError instanceof ApiError
      ? actionError.status === 401
        ? "Please sign in first so we know it's you — use the sign-in link on the My registrations page, then come back here."
        : actionError.status === 404
          ? "This registration is linked to a different email address. Sign in with the email you registered with, then try again."
          : actionError.status === 409
            ? "This registration is already marked as paid — nothing more to do."
            : `Sorry, we couldn't record that just now (error ${actionError.status}). Please try again.`
      : actionError
        ? "Sorry, we couldn't record that just now. Please try again."
        : null;

  if (!refCheck.success || unknownRef) {
    return (
      <>
        <PageHeader
          eyebrow="Payment"
          title="We couldn't find that payment"
          description="This payment link looks incomplete or invalid. Please re-open the link from your enrolment confirmation, or enrol again."
        />
        <Section>
          <Button asChild>
            <Link to={registerPath}>Go to enrolment</Link>
          </Button>
        </Section>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Payment"
        title="Complete your payment"
        description="Choose how you'd like to pay — bank transfer, cash in person, or let us know if you've already paid."
      />

      <Section width="narrow">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <CardTitle>{programTitle} — enrolment fee</CardTitle>
              {/* Same labels as /my and the enrol confirmation — a parent
                  should never see two names for the same state. */}
              {isPaid ? (
                <Badge variant="outline">Paid — confirmed</Badge>
              ) : isPending ? (
                <Badge variant="accent">Pending verification</Badge>
              ) : (
                <Badge variant="muted">Pending payment</Badge>
              )}
            </div>
            <CardDescription>
              {isPaid
                ? "This enrolment fee has been paid in full — no further payment is needed."
                : confirmingCard
                  ? "Thanks — we're just confirming your card payment with our payment provider."
                  : isPending
                    ? "We've been told to expect your payment — no further action is needed."
                    : cashConfirmed
                      ? "You've chosen to pay cash in person."
                      : "Pick the option that suits you. Your registration is already recorded — this is just the payment."}
            </CardDescription>
          </CardHeader>

          <CardContent className="flex flex-col gap-5">
            <div className="rounded-card bg-paper p-5">
              <p className="text-sm font-semibold uppercase tracking-wide text-muted">
                {isPaid ? "Amount" : "Amount due"}
              </p>
              <p className="mt-1 font-heading text-4xl font-extrabold text-ink">
                {formatAud(isPaid ? summary.data?.amount_cents ?? amountCents : amountCents)}
              </p>
              {!isPaid &&
                summary.data &&
                summary.data.balance_cents < summary.data.amount_cents && (
                  <p className="mt-1 text-sm text-muted">
                    Remaining balance of a {formatAud(summary.data.amount_cents)} fee — thanks
                    for your part-payment.
                  </p>
                )}
            </div>

            {/* ——— Just came back from Stripe, webhook still in flight ——— */}
            {confirmingCard && (
              <div
                className="flex items-start gap-3 rounded-card border border-primary/30 bg-primary/5 p-4"
                role="status"
                aria-live="polite"
              >
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                <div className="text-sm">
                  <p className="font-semibold text-ink">Confirming your payment…</p>
                  <p className="mt-1 text-muted">
                    This usually takes a few seconds. You can safely close this page — we'll email
                    you as soon as it's confirmed, and nothing is lost if you leave.
                  </p>
                </div>
              </div>
            )}

            {/* ——— Backed out of the card page ——— */}
            {cardCancelled && !isPaid && !backFromStripe && (
              <p
                className="rounded-card border border-border bg-paper p-4 text-sm text-muted"
                role="status"
              >
                No payment was taken — your card was not charged. Pick any option below to try
                again.
              </p>
            )}

            {/* ——— Settled or in-flight states ——— */}
            {isPending && (
              <div
                className="flex items-start gap-3 rounded-card border border-primary/30 bg-primary/5 p-4"
                role="status"
                aria-live="polite"
              >
                <CheckCircle2
                  className="mt-0.5 h-5 w-5 shrink-0 text-primary"
                  aria-hidden="true"
                />
                <div className="text-sm">
                  <p className="font-semibold text-ink">
                    {alreadyClaimed
                      ? "Thanks — we've noted that you've already paid."
                      : "Thanks — we've recorded your payment."}
                  </p>
                  <p className="mt-1 text-muted">
                    {alreadyClaimed ? (
                      <>
                        {hostContact} will match your payment in the records and confirm by
                        email once it's verified. No further action is needed.
                      </>
                    ) : (
                      <>
                        Your enrolment is now{" "}
                        <strong className="text-ink">pending verification</strong>. We'll
                        confirm by email once the payment clears (up to {clearDays} business
                        days). No further action is needed.
                      </>
                    )}
                  </p>
                </div>
              </div>
            )}

            {cashConfirmed && (
              <div
                className="flex items-start gap-3 rounded-card border border-primary/30 bg-primary/5 p-4"
                role="status"
                aria-live="polite"
              >
                <CheckCircle2
                  className="mt-0.5 h-5 w-5 shrink-0 text-primary"
                  aria-hidden="true"
                />
                <div className="text-sm">
                  <p className="font-semibold text-ink">Paying cash in person.</p>
                  <p className="mt-1 text-muted">
                    Hand {formatAud(amountCents)} to{" "}
                    <strong className="text-ink">{hostContact}</strong>
                    {update.isSuccess || cashWhen
                      ? ` — ${(cashWhen ?? "").toLowerCase() || "as arranged"}`
                      : ""}{" "}
                    — it'll be receipted on the spot.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      setChangingMethod(true);
                      update.reset();
                      setMethod(null);
                      setCashWhen(null);
                    }}
                  >
                    Pay a different way
                  </Button>
                </div>
              </div>
            )}

            {nothingToPay && (
              <div
                className="flex items-start gap-3 rounded-card border border-primary/30 bg-primary/5 p-4"
                role="status"
              >
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                <div className="text-sm">
                  <p className="font-semibold text-ink">There's nothing to pay.</p>
                  <p className="mt-1 text-muted">
                    This registration has no fee outstanding — you're all set.
                  </p>
                </div>
              </div>
            )}

            {/* ——— Method chooser (order + visibility are host-configured) ——— */}
            {needsChoice && (
              <div>
                <p className="mb-2 text-sm font-semibold text-ink">How would you like to pay?</p>
                {methodOrder.map((m) => (
                  <Choice
                    key={m}
                    title={METHOD_COPY[m].title}
                    desc={METHOD_COPY[m].desc(hostContact)}
                    selected={method === m}
                    onClick={() => {
                      setMethod(m);
                      if (m === "payid") setTransferDone(false);
                    }}
                  />
                ))}
              </div>
            )}

            {/* ——— Card ——— */}
            {needsChoice && method === "card" && (
              <>
                <p className="flex items-start gap-2 rounded-card border border-border bg-card p-4 text-sm text-muted">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <span>
                    Enter your card details below — they go straight to our payment provider and
                    we never see or store them. Your registration is confirmed as soon as the
                    payment goes through.
                  </span>
                </p>

                {/* Siblings still owing → offer to settle the family at once.
                    Switching scope remounts the form against a new session. */}
                {payAllCount > 1 && (
                  <div className="rounded-card border border-primary/30 bg-primary/5 p-4">
                    <p className="text-sm font-semibold text-ink">
                      Paying for {payAllCount} children?
                    </p>
                    <p className="mt-1 text-sm text-muted">
                      Settle {formatNameList(group.data?.child_names ?? [])} in one payment of{" "}
                      <strong className="text-ink">
                        {formatAud(group.data?.total_cents ?? 0)}
                      </strong>{" "}
                      — one card entry, one receipt.
                    </p>
                    <Button
                      type="button"
                      variant={cardScope === "group" ? "accent" : "outline"}
                      size="sm"
                      className="mt-3"
                      onClick={() => setCardScope(cardScope === "group" ? "single" : "group")}
                    >
                      {cardScope === "group"
                        ? `Just this one (${formatAud(amountCents)})`
                        : `Pay for all ${payAllCount} together`}
                    </Button>
                  </div>
                )}

                {/* The `key` matters: changing scope must tear the old form
                    down and fetch a new client_secret, not reuse the old one. */}
                <CardCheckout
                  key={cardScope}
                  publishableKey={stripePublishableKey}
                  fetchClientSecret={
                    cardScope === "group" ? fetchGroupClientSecret : fetchSingleClientSecret
                  }
                />
              </>
            )}

            {/* ——— Bank transfer details ——— */}
            {needsChoice && method === "payid" && (
              <>
                <dl className="flex flex-col divide-y divide-border">
                  <DetailRow label="Account name" value={bankAccountName} />
                  <DetailRow label="BSB" value={bankBsb} copyable />
                  <DetailRow label="Account number" value={bankAccountNumber} copyable />
                  <DetailRow
                    label="Payment reference"
                    value={ref}
                    copyable
                    highlight
                    hint="Add this exactly so we can match your payment."
                  />
                </dl>
                <p className="flex items-start gap-2 rounded-card border border-border bg-card p-4 text-sm text-muted">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <span>
                    Bank transfers can take time to clear. Please allow up to{" "}
                    <strong className="text-ink">{clearDays} business days</strong> for your
                    payment to be confirmed.
                  </span>
                </p>
                <CommitCheckbox
                  label="I've made the transfer"
                  checked={transferDone}
                  onChange={setTransferDone}
                />
              </>
            )}

            {/* ——— Cash handover ——— */}
            {needsChoice && method === "cash" && (
              <div>
                <p className="mb-2 text-sm font-semibold text-ink">
                  When will you hand it over?
                </p>
                {CASH_OPTIONS.map((opt) => (
                  <Choice
                    key={opt}
                    compact
                    title={opt}
                    selected={cashWhen === opt}
                    onClick={() => setCashWhen(opt)}
                  />
                ))}
              </div>
            )}

            {/* ——— Already paid ——— */}
            {needsChoice && method === "already" && (
              <div className="space-y-1.5">
                <Label htmlFor="pay-already">How and when did you pay?</Label>
                <Textarea
                  id="pay-already"
                  placeholder={`e.g. cash to ${hostContact} last Friday, or transfer on 28 June`}
                  value={alreadyNote}
                  onChange={(e) => setAlreadyNote(e.target.value)}
                />
                <p className="text-xs text-muted">
                  {hostContact} will match it in the records and confirm by email.
                </p>
              </div>
            )}

            {/* ——— Confirm ——— */}
            {needsChoice && method !== null && (
              <div>
                {needsSignIn ? (
                  <div className="rounded-card border border-border bg-paper p-4 text-sm text-muted">
                    So we know it's you, please sign in first —{" "}
                    <Link
                      to="/my"
                      className="font-semibold text-primary underline-offset-4 hover:underline"
                    >
                      get a sign-in link on the My registrations page
                    </Link>{" "}
                    (no password needed), then come back here.
                  </div>
                ) : (
                  <>
                    {actionErrorText && (
                      <p role="alert" className="mb-3 text-sm font-medium text-accent">
                        {actionErrorText}
                      </p>
                    )}
                    <Button
                      size="lg"
                      variant="accent"
                      className="w-full"
                      disabled={!canConfirm}
                      onClick={confirm}
                    >
                      {submitting
                        ? method === "card"
                          ? "Taking you to payment…"
                          : "Submitting…"
                        : method === "card"
                          ? `Pay ${formatAud(amountCents)} by card`
                          : method === "payid"
                            ? "I have paid"
                            : method === "cash"
                              ? "Confirm — I'll pay cash"
                              : "Confirm — I've already paid"}
                    </Button>
                    {!canConfirm && !submitting && (
                      <p className="mt-2 text-center text-xs text-muted">
                        {method === "payid" && "Tick “I've made the transfer” above to confirm."}
                        {method === "cash" && "Pick a handover time above to confirm."}
                        {method === "already" && "Tell us how you paid to confirm."}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </CardContent>

          {(isPaid || isPending || cashConfirmed || confirmingCard || nothingToPay) && (
            <CardFooter className="flex flex-col items-stretch gap-3 sm:flex-row">
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/my">My registrations</Link>
              </Button>
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/">Back to home</Link>
              </Button>
            </CardFooter>
          )}
        </Card>

        <p className="mt-6 text-center text-sm text-muted">
          Questions? Email us at{" "}
          <a
            className="font-semibold text-primary underline-offset-4 hover:underline"
            href={`mailto:${CONTACT_EMAIL}`}
          >
            {CONTACT_EMAIL}
          </a>
        </p>
      </Section>
    </>
  );
}

/* ────────────────────────────── building blocks ─────────────────────────── */
/* Choice + CommitCheckbox mirror the enrolment journey's (see enrol.tsx) so
 * paying later feels identical to paying during enrolment. */

function Choice({
  title,
  desc,
  selected,
  compact,
  onClick,
}: {
  title: string;
  desc?: string;
  selected: boolean;
  compact?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "mb-2 w-full rounded-card border-2 text-left transition-colors",
        compact ? "px-4 py-2.5" : "px-4 py-3",
        selected
          ? "border-primary bg-primary/5"
          : "border-border bg-card hover:border-primary/40",
      )}
    >
      <span className="flex items-center gap-2.5 text-sm font-semibold text-ink">
        <span
          aria-hidden
          className={cn(
            "h-4 w-4 shrink-0 rounded-full border-2",
            selected ? "border-primary bg-primary" : "border-border",
          )}
        />
        {title}
      </span>
      {desc && <span className="ml-[26px] mt-0.5 block text-xs text-muted">{desc}</span>}
    </button>
  );
}

function CommitCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      className={cn(
        "flex w-full items-start gap-3 rounded-card border-2 px-4 py-3 text-left transition-colors",
        checked ? "border-primary bg-primary/5" : "border-border bg-card",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border-2 text-[11px] font-extrabold text-primary-fg",
          checked ? "border-primary bg-primary" : "border-border",
        )}
      >
        {checked ? "✓" : ""}
      </span>
      <span className="text-sm font-semibold text-ink">{label}</span>
    </button>
  );
}

interface DetailRowProps {
  label: string;
  value: string;
  copyable?: boolean;
  highlight?: boolean;
  hint?: string;
}

function DetailRow({ label, value, copyable, highlight, hint }: DetailRowProps) {
  const [copied, setCopied] = React.useState(false);

  const copy = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* Clipboard unavailable (e.g. insecure context) — value is still visible. */
    }
  }, [value]);

  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0">
        <dt className="text-sm font-semibold uppercase tracking-wide text-muted">{label}</dt>
        <dd
          className={
            highlight
              ? "mt-1 font-heading text-xl font-extrabold tracking-wide text-primary"
              : "mt-1 break-words font-medium text-ink"
          }
        >
          {value}
        </dd>
        {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
      </div>
      {copyable && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={copy}
          aria-label={`Copy ${label}`}
        >
          {copied ? (
            <>
              <Check className="h-4 w-4" aria-hidden="true" /> Copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" aria-hidden="true" /> Copy
            </>
          )}
        </Button>
      )}
    </div>
  );
}
